<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：エリア割当マスタ画面
 *
 * 作成者：2021/11/22 ノア）黄
 * 最終修正者：2022/1/26 ノア）黄
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.prefectureAllocationMaster.viewTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-lr-container">
        <!-- エリア一覧 -->
        <div class="tti-tree left">
          <div class="tti-content-title">
            <span>{{ $t('master.prefectureAllocationMaster.areaAllocationList') }}</span>
          </div>
          <div class="content">
            <div @click="showArea = !showArea">
              <span class="expander">
                <span v-if="showArea"><tti-svg-icon icon-name="su_021"></tti-svg-icon></span>
                <span v-else><tti-svg-icon icon-name="su_022"></tti-svg-icon></span>
              </span>
              <span>{{ $t('master.prefectureAllocationMaster.area') }}</span>
            </div>
            <div class="customTree">
              <dragableTree
                :disabled="pageLock"
                v-show="showArea && showAreaTree"
                :data="areaMasterTree"
                @disableDragCallback="onDisableDragCallback"
              ></dragableTree>
              <div class="tree-empty-text" v-if="!showAreaTree">
                {{ $t('messages.common.emptyTableData') }}
              </div>
            </div>
          </div>
        </div>
        <!-- 都道府県一覧 -->
        <div class="tti-tree right">
          <div class="tti-content-title">
            <span>{{ $t('master.prefectureAllocationMaster.prefectureList') }}</span>
          </div>
          <div class="content">
            <div @click="showPrefecture = !showPrefecture">
              <span class="expander">
                <span v-if="showPrefecture"><tti-svg-icon icon-name="su_021"></tti-svg-icon></span>
                <span v-else><tti-svg-icon icon-name="su_022"></tti-svg-icon></span>
              </span>
              <span>{{ $t('master.prefectureAllocationMaster.unAllocation') }}</span>
            </div>
            <div class="customTree">
              <dragableTree :disabled="pageLock" v-show="showPrefecture" :data="unAllocationMasterData"></dragableTree>
              <div class="tree-empty-text" v-if="!unAllocationMasterData || unAllocationMasterData.length === 0">
                {{ $t('messages.common.emptyTableData') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 取消 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onCancelButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.cancel)"
          ><tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}</el-button
        >
      </div>
      <div class="tti-bottom-right-area">
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.save)"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
        </el-button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component } from 'vue-property-decorator'
import { cloneDeep, debounce } from 'lodash'

import { i18n } from '@/locales'
import { getAreaAllocationMasterData, updateAreaAllocationMasterData } from '@/store/apis'
import { TreeData, PrefectureAllocData, MasterUpdateParam, NoDragNode } from '@/models'
import DragableTree from '@/components/DraggableTree.vue'
import MST_BASE from '@/views/master/MST_BASE.vue'
import { msgBoxService } from '@/commons/services/msgBoxService'

/**
 * エリア割当マスタ画面
 *
 * @class
 */
@Component({
  name: 'mst-pref-alloc',
  components: { DragableTree }
})
export default class MST_PREF_ALLOC extends MST_BASE {
  /**
   * 画面ID
   */
  pageId = 'MST_PREF_ALLOC'

  /**
   * 画面名
   */
  pageName = i18n.t('master.prefectureAllocationMaster.viewTitle').toString()

  /**
   * エリアマスタリスト
   */
  prefectureAllocData: PrefectureAllocData[] = []

  /**
   * エリアマスタTree
   */
  areaMasterTree: TreeData[] = []

  /**
   * 未割当Tree
   */
  unAllocationMasterData: TreeData[] = []

  showArea = true
  showPrefecture = true

  // eslint-disable-next-line
  onDisableDragCallback = (noDragNode: NoDragNode) => {}

  /**
   * エリアTreeを表示するかどうか
   *
   * @returns {boolean} 表示(true)
   */
  get showAreaTree(): boolean {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!this.tableData || !this.tableData.areaList || this.tableData.areaList.length === 0) {
      return false
    }

    return true
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('MST_PREF_ALLOC > mounted')
    this.fetchTableData()
    this.checkPageLock()

    this.onDisableDragCallback = debounce((noDragNode: NoDragNode) => {
      if (!noDragNode || noDragNode.level !== '1') {
        return
      }
      msgBoxService.info(
        i18n
          .t('master.prefectureAllocationMaster.cannotAllowMessage', [
            noDragNode.label,
            i18n.t('master.prefectureAllocationMaster.managementPlace')
          ])
          .toString()
      )
    }, 300)
  }

  /**
   * テーブルデータを検索して、初期化を実行する。
   *
   * @returns {Promise<boolean>} 検索実行結果
   */
  async fetchTableData(): Promise<boolean> {
    try {
      this.$log.debug(`MST_BASE > ${this.pageId} > fetchTableData`)
      const data = (await getAreaAllocationMasterData()) as PrefectureAllocData
      this.initAllocData(data)
      this.fetchTableDataAfter()
      return true
    } catch (e) {
      this.$log.debug(`MST_BASE > ${this.pageId} > fetchTableData: e=`, e)
      throw e
    }
  }

  /**
   * テーブルデータの初期化を実行する。
   *
   * @param {PrefectureAllocData} data 検索されたデータ
   * @returns {void}
   */
  initAllocData(data: PrefectureAllocData): void {
    if (!data) {
      data = {} as PrefectureAllocData
    }
    this.oldData = cloneDeep(data)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.tableData = data
  }

  fetchTableDataAfter(): void {
    this.multipleSelection = []
    this.changePrefectureAllocDataToTree()
  }

  /**
   * エリアマスタをTreeDataに変換する
   *
   * @returns {void}
   */
  changePrefectureAllocDataToTree(): void {
    this.$log.debug('MST_PREF_ALLOC > changeAreaMasterToTree')
    this.areaMasterTree = (this.tableData as unknown as PrefectureAllocData).areaList.map(item => {
      return {
        id: item.id,
        label: item.areaName,
        expand: true,
        disabled: true,
        isLastNode: false,
        level: '0',
        children: item.prefectureList?.map(prefecture => {
          return {
            id: prefecture.id,
            label: prefecture.prefectureName,
            disabled: prefecture.hasManagementPlace,
            level: '1',
            isLastNode: true,
            children: []
          }
        })
      }
    })

    this.unAllocationMasterData = (this.tableData as unknown as PrefectureAllocData).unAllocationList.map(item => {
      return {
        id: item.id,
        label: item.prefectureName,
        disabled: false,
        isLastNode: true,
        children: []
      }
    })
  }

  /**
   * 取消ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onCancelButtonClick(): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > onCancelButtonClick`)

    if (!this.checkDataChanged()) {
      return
    }

    const confirmCallback = () => {
      this.tableData = cloneDeep(this.oldData)
      this.changePrefectureAllocDataToTree()
    }

    msgBoxService.confirm(i18n.t('messages.masterCommon.cancelConfirm').toString(), confirmCallback)
  }

  /**
   * 変更されましたデータを算出する。
   *
   * @returns {any} 変更されたデータ
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getChangedData(): any {
    const areaMasterTree = this.areaMasterTree.map(item => {
      return {
        id: item.id,
        prefectureList: item.children?.map(prefecture => {
          return {
            id: prefecture.id
          }
        })
      }
    })

    const updateDataList: { areaId: string; prefectureId: string }[] = []

    ;(this.tableData as unknown as PrefectureAllocData).areaList.forEach(item => {
      const areaId = item.id
      const newPerfectureList = areaMasterTree.find(area => area.id === areaId)?.prefectureList
      const oldPerfectureList = item.prefectureList
      if (newPerfectureList) {
        newPerfectureList.forEach(newPerfecture => {
          const index = oldPerfectureList?.findIndex(oldPerfecture => oldPerfecture.id === newPerfecture.id)
          if (index === -1) {
            updateDataList.push({
              areaId: areaId,
              prefectureId: newPerfecture.id
            })
          }
        })
      }
    })

    const unAllocationList: string[] = []

    this.unAllocationMasterData.forEach(item => {
      const prefectureId = item.id
      const index = (this.tableData as unknown as PrefectureAllocData).unAllocationList.findIndex(
        unAllocation => unAllocation.id === prefectureId
      )
      if (index === -1) {
        unAllocationList.push(prefectureId)
      }
    })

    return {
      updateDataList,
      unAllocationList
    }
  }

  /**
   * データが変更されましたかをチェックする。
   *
   * @returns {boolean} 変更されているかどうか
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  checkDataChanged(): boolean {
    const { updateDataList, unAllocationList } = this.getChangedData()

    if ((!updateDataList || updateDataList.length === 0) && (!unAllocationList || unAllocationList.length === 0)) {
      return false
    }

    return true
  }

  /**
   * 保存ボタンをクリックした時の処理
   *
   * @returns {void}
   */
  async onSaveButtonClick(): Promise<void> {
    this.$log.debug(`MST_BASE > ${this.pageId} > onSaveButtonClick`)
    try {
      await this.saveTableData()
    } catch (e) {
      this.$log.debug(`MST_BASE > ${this.pageId} > onSaveButtonClick: e=`, e)
      throw e
    }
  }

  /**
   * 保存ボタンをクリックした時の処理
   *
   * @returns {Promise<boolean>} 保存結果
   */
  async saveTableData(): Promise<boolean> {
    try {
      this.$log.debug(`MST_BASE > ${this.pageId} > saveTableData`)

      const { updateDataList, unAllocationList } = this.getChangedData()

      if ((!updateDataList || updateDataList.length === 0) && (!unAllocationList || unAllocationList.length === 0)) {
        msgBoxService.info(i18n.t('messages.masterCommon.dataNotChanged').toString())
        return false
      }

      const params = {
        updateDataList,
        unAllocationList
      } as MasterUpdateParam

      await updateAreaAllocationMasterData(params)
      this.saveTableDataAfter()

      return true
    } catch (e) {
      this.$log.debug(`MST_BASE > ${this.pageId} > saveTableData: e=`, e)
      throw e
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.customTree {
  height: inherit;

  > ul {
    padding-left: 0;
    margin-top: 0;
    white-space: nowrap;
  }
}

.tti-tree {
  overflow: auto;

  .tti-content-title {
    font-size: $FS02;
  }

  ::v-deep ul {
    padding-left: 24px;
  }

  ::v-deep .content {
    > ul {
      padding-left: 0;
      margin-top: 0;
      white-space: nowrap;
    }

    span {
      display: inline-block;
      padding: 4px 0;
    }
  }
}
</style>
